import type React from 'react'

/**
 * Severity levels shared by the toast provider and its helpers.
 */
export type ErrorSeverity = 'error' | 'warning' | 'info' | 'success'

/**
 * Toast configuration. `actionUrl` renders a react-router `<Link>` action
 * (RULES navigation: never `onClick`/`navigate()` for nav); `action` is an
 * escape hatch for a custom node when a link isn't appropriate.
 */
export interface ErrorToastConfig {
  message: string
  severity?: ErrorSeverity
  title?: string
  /** Secondary line rendered muted under `message` (e.g. an error detail). */
  subMessage?: string
  duration?: number
  persistent?: boolean
  action?: React.ReactNode
  actionUrl?: string
  actionLabel?: string
  /** Fired once when the toast leaves the screen (auto-hide or dismiss). */
  onClose?: () => void
}

/**
 * Extract a human-readable message from the various error shapes the API
 * and axios produce.
 */
export const extractErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail
  }

  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  if (error?.response?.data?.error) {
    return error.response.data.error
  }

  if (error?.message) {
    return error.message
  }

  if (error?.response?.statusText) {
    return `${error.response.status}: ${error.response.statusText}`
  }

  return 'An unexpected error occurred'
}

/**
 * Map an API error to a toast, choosing title/severity/persistence by status.
 */
export const handleApiErrorWithToast = (
  error: any,
  showError: (config: ErrorToastConfig | string) => void,
  customMessage?: string,
) => {
  const message = customMessage || extractErrorMessage(error)
  const status = error?.response?.status

  let severity: ErrorSeverity = 'error'
  let title = 'Error'

  if (status === 401) {
    title = 'Authentication Required'
    severity = 'warning'
  } else if (status === 403) {
    title = 'Permission Denied'
  } else if (status === 404) {
    title = 'Not Found'
  } else if (status === 422) {
    title = 'Validation Error'
  } else if (status >= 500) {
    title = 'Server Error'
  }

  showError({
    message,
    title,
    severity,
    persistent: status === 401 || status === 403,
  })

  return message
}
